import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, RawHtml } from '../../../../common/index.jsx';
import { CellLayout } from '../../../../common/index.jsx';
import { formattedFullName } from '../../../../../utils/helpers/CommonHelper.jsx';
import ScheduleDownloadButton from './ScheduleDownloadButton.jsx';
import ProtectedEmailLink from '../../../../common/ProtectedEmailLink/ProtectedEmailLink.jsx';

export default function WorkplaceEmployeesAndStudents({ department }) {
  const { t } = useTranslation();

  const { students, employees } = useMemo(
    () => ({
      students: department.m_phdstudents,
      employees: department.m_employees,
    }),
    [department.m_employees, department.m_phdstudents],
  );

  return (
    <>
      {!employees || employees.length === 0 ? null : (
        <CollapseBlock title={t('Zamestnanci')} collapsible>
          <EmployeeCells modelArray={employees} />
        </CollapseBlock>
      )}

      {!students || students.length === 0 ? null : (
        <CollapseBlock collapsible title={t('Interní doktorandi')}>
          <EmployeeCells modelArray={students} />
        </CollapseBlock>
      )}
    </>
  );
}

function EmployeeCells({ modelArray }) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  // Detect mobile viewport
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
    } else {
      // Fallback for older browsers without addEventListener on MediaQueryList
      mq.onchange = handler;
    }
    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', handler);
      } else {
        mq.onchange = null;
      }
    };
  }, []);

  return (modelArray || []).map((model, index) => {
    const rowKey = model.uidnumber ?? model.person?.id ?? model.person?.person_id ?? index;

    const nameBlock = (
      <strong style={{ fontSize: '15px' }}>
        {formattedFullName(model.person)}
      </strong>
    );

    const iconsBlock = (
      <div
        className="teacher-table-schedule-items"
        style={{
          display: 'flex',
          justifyContent: isMobile ? 'flex-end' : undefined,
          alignItems: isMobile ? 'flex-end' : 'center',
        }}
      >
        <div style={{ marginRight: '8px', verticalAlign: 'baseline' }}>
          <ProtectedEmailLink user={model.person.u} domain={model.person.d} />
        </div>
        {model.homepage != null && model.homepage !== '' ? (
          <div>
            <a
              className="mr-2"
              href={model.homepage}
              title={t('Osobná stránka')}
            >
              <i className="fas fa-globe-europe" />
            </a>
          </div>
        ) : null}
        <div>
          <a
            href={`https://vzdelavanie.uniza.sk/vzdelavanie/rozvrh2.php?sq=1&id=${model.uidnumber}`}
            target="_blank"
            rel="noreferrer"
            title={t('Aktuálny rozvrh')}
            className="mr-2"
          >
            <i className="fas fa-calendar-alt"></i>
          </a>
        </div>
        <div>
          <ScheduleDownloadButton
            uid={model.uidnumber}
            alarm={'0'}
            fullName={`${model.person.last_name}_${model.person.first_name}`}
          />
        </div>
      </div>
    );

    if (isMobile) {
      // Mobile layout: 1) icons right, 2) name full width, 3) phone, 4) room with icons on right
      return (
        <div className="mt-10px" key={`employee-mobile-${rowKey}-${index}`}>
          <CellLayout layout="two-cells" className="bgAltBg" gap="none">
            <CellLayout.Cell style={{ width: '85%' }}>
              {nameBlock}
            </CellLayout.Cell>
            <CellLayout.Cell style={{ width: '15%', paddingRight: 0 }}>
              {iconsBlock}
            </CellLayout.Cell>
          </CellLayout>
          <CellLayout className="bgAltBg">
            <CellLayout.Cell>
              {t('Telefónne číslo')}: {model.phone}
            </CellLayout.Cell>
          </CellLayout>
          <CellLayout className="bgAltBg">
            <CellLayout.Cell>
              {t('Miestnosť')}: {model.room}
            </CellLayout.Cell>
          </CellLayout>
          <CellLayout>
            {/* Zobraz iba ak je popis v správnom jazyku */}
            {(!isEnglish && model.description_sk) ||
            (isEnglish && model.description_en) ? (
              <CellLayout.Cell>
                <RawHtml>
                  {isEnglish
                    ? model.description_en?.replace(/\n/g, '</br>')
                    : model.description_sk?.replace(/\n/g, '</br>')}
                </RawHtml>
              </CellLayout.Cell>
            ) : null}
          </CellLayout>
        </div>
      );
    }

    // Desktop layout (original)
    return (
      <div className="mt-10px" key={`employee-desktop-${rowKey}-${index}`}>
        <CellLayout layout="two-cells" className="workplaces-row bgAltBg">
          <CellLayout.Cell>{nameBlock}</CellLayout.Cell>
          <CellLayout.Cell style={{ paddingRight: 0 }}>
            {iconsBlock}
          </CellLayout.Cell>
        </CellLayout>
        <CellLayout layout="two-cells" className="bgAltBg">
          <CellLayout.Cell>
            {t('Telefónne číslo')}: {model.phone}
          </CellLayout.Cell>
          <CellLayout.Cell style={{ textAlign: 'right' }}>
            {t('Miestnosť')}: {model.room}
          </CellLayout.Cell>
        </CellLayout>
        <CellLayout style={{ marginBottom: '10px', marginTop: '10px' }}>
          {/* Zobraz iba ak je popis v správnom jazyku */}
          {(!isEnglish && model.description_sk) ||
          (isEnglish && model.description_en) ? (
            <CellLayout.Cell>
              <RawHtml>
                {isEnglish
                  ? model.description_en?.replace(/\n/g, '</br>')
                  : model.description_sk?.replace(/\n/g, '</br>')}
              </RawHtml>
            </CellLayout.Cell>
          ) : null}
        </CellLayout>
      </div>
    );
  });
}
